<template>
  <!--
    SLOTS:
      bottom
      empty
      tableHeader
      no-data
  -->
  <div class="table-v2-custom" :class="[themeClass(), {'table-v2-custom--responsive': responsive}]">
    <div class="table-v2-inner-spacing">
      <!-- Table Header Slot -->
      <div v-if="$slots.tableHeader" class="table-header">
        <slot name="tableHeader"></slot>
      </div>

      <!-- Vuetify Data Table -->
      <v-data-table
        :headers="computedHeaders"
        :items="data || []"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :loading="loading"
        :hide-default-footer="hideFooter"
        :items-per-page="itemsPerPage"
        :class="tableClasses"
        @update:sort-by="handleSortChange"
        @update:sort-desc="handleSortDescChange"
        ref="table">

        <!-- Custom item slot for each row -->
        <template v-slot:item="{ item, index }">
          <tr :class="getRowClass(index)">
            <td v-for="header in computedHeaders" :key="header.value" :class="getCellClass(header)">
              <slot :name="`item.${header.value}`" :item="item" :value="item[header.value]">
                {{ formatCellValue(item, header) }}
              </slot>
            </td>
          </tr>
        </template>

        <!-- No data slot -->
        <template v-slot:no-data>
          <slot name="empty">
            <div class="text-center pa-4">
              No data available
            </div>
          </slot>
        </template>

        <!-- Loading slot -->
        <template v-slot:loading>
          <slot name="loading">
            <div class="text-center pa-4">
              Loading...
            </div>
          </slot>
        </template>
      </v-data-table>

      <!-- Bottom slot -->
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    // Data props
    data: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      default: () => []
    },
    // Legacy v2-table props for compatibility
    sort: {
      type: Object,
      default: () => ({})
    },
    // Style props
    theme: {
      type: String,
      default: 'dark'
    },
    responsive: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    hideFooter: {
      type: Boolean,
      default: false
    },
    itemsPerPage: {
      type: Number,
      default: -1 // Show all items by default
    },
    // Legacy props (kept for compatibility)
    hoverOverlayComponent: String,
    bottomOverlayComponent: String,
    externalFiltering: Function,
    overlayColumnStart: Number,
    lazyLoad: Boolean,
    tableType: Number,
    rowKey: String,
    refreshTable: Boolean
  },
  data() {
    return {
      sortBy: null,
      sortDesc: false,
      columnDefinitions: []
    }
  },
  computed: {
    table() {
      return this.$refs.table
    },
    computedHeaders() {
      // If headers are explicitly provided, use them
      if (this.headers && this.headers.length > 0) {
        return this.headers
      }

      // Otherwise, try to extract from slot content (for v2-table-column compatibility)
      return this.columnDefinitions
    },
    tableClasses() {
      return [
        'styled-data-table',
        `styled-data-table--${this.theme}`,
        {
          'styled-data-table--responsive': this.responsive
        }
      ]
    }
  },
  mounted() {
    // Initialize sort from legacy sort prop
    if (this.sort && this.sort.prop) {
      this.sortBy = this.sort.prop
      this.sortDesc = this.sort.order === 'descending'
    }

    // Extract column definitions from child components (v2-table-column compatibility)
    this.extractColumnDefinitions()
  },
  methods: {
    themeClass() {
      return `table-v2-custom--${this.theme}-theme`
    },
    handleSortChange(sortBy) {
      this.sortBy = sortBy
      this.emitSortChange()
    },
    handleSortDescChange(sortDesc) {
      this.sortDesc = sortDesc
      this.emitSortChange()
    },
    emitSortChange() {
      const sortData = {
        prop: this.sortBy,
        order: this.sortDesc ? 'descending' : 'ascending'
      }
      this.$emit('sort-change', sortData)
    },
    extractColumnDefinitions() {
      // This method will be called to extract column definitions from slots
      // For now, we'll create a basic structure
      if (this.data && this.data.length > 0) {
        const firstItem = this.data[0]
        this.columnDefinitions = Object.keys(firstItem).map(key => ({
          text: key.charAt(0).toUpperCase() + key.slice(1),
          value: key,
          sortable: true
        }))
      }
    },
    formatCellValue(item, header) {
      const value = item[header.value]
      return value !== null && value !== undefined ? value : ''
    },
    getRowClass(index) {
      return [
        'styled-table-row',
        {
          'styled-table-row--even': index % 2 === 0,
          'styled-table-row--odd': index % 2 === 1
        }
      ]
    },
    getCellClass(header) {
      return [
        'styled-table-cell',
        `styled-table-cell--${header.value}`
      ]
    }
  }
}

</script>

<style lang="scss">
.table-v2-custom {
  height: 100%;

  .table-v2-inner-spacing {
    margin: 0 10px;
    height: 100%;
  }

  .table-header {
    margin-bottom: 16px;
  }

  &--responsive {
    .v-data-table {
      width: 100% !important;
    }
  }

  // Light theme (for viewership tables)
  &--light-theme {
    $header-color: #f5f5f5;
    $primary-color: #ffffff;
    $secondary-color: #fafafa;
    $hover-over-color: #e0e0e0;
    $text-color: rgba(0, 0, 0, 0.87);

    background: $primary-color;

    .styled-data-table {
      background: $primary-color;

      .v-data-table__wrapper {
        background: $primary-color;
      }

      thead tr th {
        background: $header-color !important;
        color: $text-color !important;
        font-weight: 600;
        text-transform: uppercase;
        border-bottom: 1px solid #e0e0e0 !important;
      }

      tbody tr {
        &:nth-child(odd) {
          background: $secondary-color;
        }

        &:nth-child(even) {
          background: $primary-color;
        }

        &:hover {
          background-color: $hover-over-color !important;
        }

        td {
          color: $text-color;
          border-bottom: 1px solid #e0e0e0;
        }
      }
    }
  }

  // Orange theme
  &--orange-theme {
    $header-color: #8F8F8F;
    $primary-color: #DFDFDF;
    $secondary-color: #BFBFBF;
    $hover-over-color: #AFAFAF;
    $text-color: black;

    background: $primary-color;

    .styled-data-table {
      background: $primary-color;

      .v-data-table__wrapper {
        background: $primary-color;
      }

      thead tr th {
        background: $header-color !important;
        color: $text-color !important;
        font-weight: 600;
        text-transform: uppercase;
        border-bottom: none !important;
      }

      tbody tr {
        font-size: 16px;

        &:nth-child(odd) {
          background: $secondary-color;
        }

        &:nth-child(even) {
          background: $primary-color;
        }

        &:hover {
          background-color: $hover-over-color !important;
        }

        td {
          color: $text-color;
          border-bottom: none;
        }
      }
    }
  }

  // Dark theme (default)
  &--dark-theme {
    $primary-color: #607d8b; // blue-grey light
    $secondary-color: #455a64; // blue-grey dark
    $header-color: #263238; // blue-grey very-dark
    $text-color: #ffffff;
    $text-light: #f5f5f5;

    background: $primary-color;

    .styled-data-table {
      background: $primary-color;

      .v-data-table__wrapper {
        background: $primary-color;
      }

      thead tr th {
        background: $primary-color !important;
        color: $text-light !important;
        font-weight: 600;
        text-transform: uppercase;
        border-bottom: none !important;
      }

      tbody tr {
        font-size: 16px;

        &:nth-child(odd) {
          background: $secondary-color;
        }

        &:nth-child(even) {
          background: $primary-color;
        }

        &:hover {
          background-color: rgb(48,54,61) !important;
        }

        &.styled-table-row--active {
          background-color: rgb(48,54,61) !important;
        }

        td {
          color: $text-color;
          border-bottom: none;
        }
      }
    }

    .table-header {
      background: $header-color;
      color: $text-color;
      padding: 16px;
      display: flex;
      align-items: center;
      min-height: 70px;
    }
  }
}

// Legacy overlay styles
.row_hover_overlay__background {
  background: rgba(0, 0, 0, 0.75);
}

// Additional Vuetify overrides for consistent styling
.styled-data-table {
  .v-data-table-header th {
    font-size: 14px !important;
  }

  .v-data-table__wrapper tbody tr td {
    font-size: 16px;
  }
}

</style>



// WEBPACK FOOTER //
// StyledTable.vue?0fa2cf76